import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { SavedCommunity } from '@creanizer/types'
import { createIndexedDBStorage } from '@creanizer/utils'
import { generateId } from '../utils/generateId'

interface CommunityStore {
  savedCommunities: SavedCommunity[]
  currentCommunity: SavedCommunity | null
  maxCommunities: number
  
  // CRUD操作
  addCommunity: (community: Omit<SavedCommunity, 'id' | 'createdAt' | 'updatedAt'>) => void
  updateCommunity: (id: string, updates: Partial<SavedCommunity>) => void
  deleteCommunity: (id: string) => void
  getCommunity: (id: string) => SavedCommunity | undefined
  clearCommunities: () => void
  clearItems: () => void
  
  // 現在のコミュニティ
  setCurrentCommunity: (community: SavedCommunity | null) => void
  
  // エクスポート/インポート
  exportAsText: () => void
  exportAsJSON: () => void
  exportSingleAsText: (id: string) => void
  exportSingleAsJSON: (id: string) => void
  importFromJSON: (jsonString: string) => void
  
  // 検索・フィルタ
  searchCommunities: (query: string) => SavedCommunity[]
  getCommunitiesByWorld: (worldId: string) => SavedCommunity[]
  getChildCommunities: (parentId: string) => SavedCommunity[]
}

export const useCommunityStore = create<CommunityStore>()(
  persist(
    (set, get) => ({
      savedCommunities: [],
      currentCommunity: null,
      maxCommunities: 500,
      
      addCommunity: (community) => {
        const state = get()
        if (state.savedCommunities.length >= state.maxCommunities) {
          throw new Error(`保存できるコミュニティの上限（${state.maxCommunities}件）に達しています`)
        }
        
        const newCommunity: SavedCommunity = {
          ...community,
          id: generateId('com'),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }
        
        set((state) => ({
          savedCommunities: [newCommunity, ...state.savedCommunities]
        }))
      },
      
      updateCommunity: (id, updates) => {
        set((state) => ({
          savedCommunities: state.savedCommunities.map((community) =>
            community.id === id
              ? { ...community, ...updates, updatedAt: new Date().toISOString() }
              : community
          ),
        }))
      },
      
      deleteCommunity: (id) => {
        set((state) => ({
          savedCommunities: state.savedCommunities.filter((c) => c.id !== id),
          currentCommunity: state.currentCommunity?.id === id ? null : state.currentCommunity,
        }))
      },
      
      getCommunity: (id) => {
        return get().savedCommunities.find((c) => c.id === id)
      },
      
      clearCommunities: () => {
        set({ savedCommunities: [], currentCommunity: null })
      },
      
      clearItems: () => {
        set({ savedCommunities: [], currentCommunity: null })
      },
      
      setCurrentCommunity: (community) => {
        set({ currentCommunity: community })
      },
      
      exportAsText: () => {
        const { savedCommunities } = get()
        if (savedCommunities.length === 0) {
          alert('保存されたコミュニティがありません。')
          return
        }
        const text = formatCommunitiesForExport(savedCommunities)
        
        // ダウンロード処理
        const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `saved_communities_${Date.now()}.txt`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      },
      
      exportAsJSON: () => {
        const { savedCommunities } = get()
        if (savedCommunities.length === 0) {
          alert('保存されたコミュニティがありません。')
          return
        }
        
        const exportData = {
          version: '1.0.0',
          type: 'communities',
          exportDate: new Date().toISOString(),
          count: savedCommunities.length,
          data: savedCommunities
        }
        
        const json = JSON.stringify(exportData, null, 2)
        const blob = new Blob([json], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `saved_communities_${Date.now()}.json`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      },
      
      exportSingleAsText: (id: string) => {
        const community = get().getCommunity(id)
        if (!community) {
          alert('コミュニティが見つかりません。')
          return
        }
        
        const text = formatCommunitiesForExport([community])
        const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `community_${community.result.basicInfo.name}_${Date.now()}.txt`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      },
      
      exportSingleAsJSON: (id: string) => {
        const community = get().getCommunity(id)
        if (!community) {
          alert('コミュニティが見つかりません。')
          return
        }
        
        const exportData = {
          version: '1.0.0',
          type: 'community',
          exportDate: new Date().toISOString(),
          data: community
        }
        
        const json = JSON.stringify(exportData, null, 2)
        const blob = new Blob([json], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `community_${community.result.basicInfo.name}_${Date.now()}.json`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      },
      
      importFromJSON: (jsonString) => {
        try {
          const imported = JSON.parse(jsonString)
          
          // 複数の形式をサポート
          let communities: SavedCommunity[]
          if (imported.type === 'community' && imported.data && !Array.isArray(imported.data)) {
            // 単独コミュニティの場合
            communities = [imported.data as SavedCommunity]
          } else {
            // 複数コミュニティの場合
            communities = (
              imported.data?.communities ||  // VersionManager変換後の形式
              (imported.type === 'communities' ? imported.data : null) ||  // 個別エクスポート形式
              imported.data ||  // BulkDataManager形式
              imported.savedCommunities ||  // 古い形式
              (Array.isArray(imported) ? imported : [])  // 直接配列形式
            ) as SavedCommunity[]
          }
          
          const validCommunities = communities
            .filter(c => c.result && c.result.basicInfo)
            .map(c => {
              const result = c.result
              
              // 古い構造から新しい構造への変換（型チェックを回避）
              const oldResult = result as any
              if (oldResult.geography || oldResult.economy || oldResult.culture || oldResult.politics || oldResult.problems) {
                // 古い構造を検出した場合、新しい構造に変換
                const convertedResult = {
                  basicInfo: oldResult.basicInfo,
                  features: {
                    geography: oldResult.geography ? 
                      `${oldResult.geography.location || ''}。地形: ${oldResult.geography.terrain?.join('、') || ''}。気候: ${oldResult.geography.climate || ''}` : 
                      '地理情報なし',
                    facilities: oldResult.facilities?.mainFacilities ? 
                      oldResult.facilities.mainFacilities.map((f: any) => f.name).join('、') : 
                      '主要施設情報なし',
                    culture: oldResult.culture ? 
                      `言語: ${oldResult.culture.languages?.map((l: any) => l.name).join('、') || ''}。宗教: ${oldResult.culture.religions?.map((r: any) => r.name).join('、') || ''}` : 
                      '文化情報なし',
                    problems: oldResult.problems?.currentIssues ? 
                      oldResult.problems.currentIssues.map((i: any) => `${i.type}: ${i.description}`).join('。') : 
                      '特に問題なし',
                    politics: oldResult.politics ? 
                      `${oldResult.politics.governmentType || ''}。${oldResult.politics.ruler ? `統治者: ${oldResult.politics.ruler.name}` : ''}` : 
                      '政治情報なし',
                    economy: oldResult.economy ? 
                      `豊かさ: ${oldResult.economy.wealthLevel || ''}。主要産業: ${oldResult.economy.mainIndustries?.map((i: any) => i.name).join('、') || ''}` : 
                      '経済情報なし'
                  },
                  residents: {
                    totalPopulation: oldResult.residents?.totalPopulation || oldResult.basicInfo.population || 0,
                    characteristics: oldResult.residents?.characteristics || '一般的な住民',
                    notableResidents: oldResult.residents?.notableResidents?.map((r: any) => ({
                      name: r.name,
                      occupation: r.occupation,
                      description: r.description || r.personality || ''
                    })) || []
                  },
                  politicsEconomy: {
                    politics: oldResult.politics ? 
                      `${oldResult.politics.governmentType || ''}。${oldResult.politics.ruler ? `統治者: ${oldResult.politics.ruler.name}` : ''}` : 
                      '政治情報なし',
                    economy: oldResult.economy ? 
                      `豊かさ: ${oldResult.economy.wealthLevel || ''}。主要産業: ${oldResult.economy.mainIndustries?.map((i: any) => i.name).join('、') || ''}` : 
                      '経済情報なし'
                  },
                  history: {
                    foundingStory: oldResult.history?.founding ? 
                      `${oldResult.history.founding.year}年に${oldResult.history.founding.founder}によって創立。${oldResult.history.founding.story || ''}` : 
                      '創立物語なし',
                    timeline: oldResult.history?.majorEvents?.map((e: any) => ({
                      year: e.year,
                      event: e.event
                    })) || []
                  },
                  anecdotes: oldResult.anecdotes || {
                    localLegends: [],
                    famousIncidents: [],
                    strangeOccurrences: []
                  },
                  metadata: oldResult.metadata || {
                    generatedAt: new Date().toISOString(),
                    version: '1.0'
                  }
                }
                
                return {
                  ...c,
                  result: convertedResult,
                  id: c.id || generateId('com'),
                  createdAt: c.createdAt || new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                }
              }
              
              // 既に新しい構造の場合はそのまま使用
              return {
                ...c,
                id: c.id || generateId('com'),
                createdAt: c.createdAt || new Date().toISOString(),
                updatedAt: c.updatedAt || new Date().toISOString(),
              }
            })
          
          // 現在のコミュニティと重複チェック（名前と正式名称で判定）
          const currentCommunities = get().savedCommunities
          const uniqueCommunities = validCommunities.filter((newCommunity: any) => {
            return !currentCommunities.some(existing => 
              existing.result.basicInfo.name === newCommunity.result.basicInfo.name && 
              existing.result.basicInfo.fullName === newCommunity.result.basicInfo.fullName
            )
          })
          
          // 既存データにマージ（最大数を超えたら古いものを削除）
          const { maxCommunities } = get()
          let mergedCommunities = [...uniqueCommunities, ...currentCommunities]
          if (mergedCommunities.length > maxCommunities) {
            mergedCommunities = mergedCommunities.slice(0, maxCommunities)
          }
          
          set({ savedCommunities: mergedCommunities })
        } catch (error) {
          throw new Error('インポートデータの形式が不正です')
        }
      },
      
      searchCommunities: (query) => {
        const q = query.toLowerCase()
        return get().savedCommunities.filter(c => 
          c.title.toLowerCase().includes(q) ||
          c.result.basicInfo.name.toLowerCase().includes(q) ||
          c.result.basicInfo.fullName?.toLowerCase().includes(q) ||
          c.tags?.some(tag => tag.toLowerCase().includes(q))
        )
      },
      
      getCommunitiesByWorld: (worldId) => {
        return get().savedCommunities.filter(c => c.worldId === worldId)
      },
      
      getChildCommunities: (parentId) => {
        return get().savedCommunities.filter(c => c.parentCommunityId === parentId)
      },
    }),
    {
      name: 'community-storage',
      version: 1,
      storage: createIndexedDBStorage('community-storage'),
      partialize: (state) => ({ 
        savedCommunities: state.savedCommunities,
        currentCommunity: state.currentCommunity
      })
    }
  )
)

// コミュニティをテキスト形式にフォーマット
function formatCommunitiesForExport(communities: SavedCommunity[]): string {
  const date = new Date().toLocaleString('ja-JP')
  
  let text = '=== コミュニティリスト ===\n'
  text += `エクスポート日時: ${date}\n`
  text += `合計: ${communities.length}件\n`
  text += '='.repeat(50) + '\n\n'

  communities.forEach((community, index) => {
    const result = community.result
    text += `[${index + 1}] ${community.title}\n`
    text += '━'.repeat(50) + '\n'
    
    text += `【基本情報】\n`
    text += `名前: ${result.basicInfo.name}\n`
    text += `正式名称: ${result.basicInfo.fullName || result.basicInfo.name}\n`
    text += `タイプ: ${result.basicInfo.type}\n`
    text += `規模: ${result.basicInfo.scale}\n`
    text += `人口: ${result.basicInfo.population.toLocaleString()}人\n`
    text += `雰囲気: ${result.basicInfo.atmosphere}\n`
    
    text += `\n【特徴】\n`
    text += `地理: ${result.features.geography}\n`
    text += `施設: ${result.features.facilities}\n`
    text += `文化: ${result.features.culture}\n`
    text += `問題: ${result.features.problems}\n`
    
    text += `\n【住民】\n`
    text += `総人口: ${result.residents.totalPopulation.toLocaleString()}人\n`
    text += `特徴: ${result.residents.characteristics}\n`
    
    if (result.residents.notableResidents?.length) {
      text += `\n著名な住民:\n`
      result.residents.notableResidents.forEach(resident => {
        text += `  ◆ ${resident.name}（${resident.occupation}）\n`
        text += `    ${resident.description}\n`
      })
    }
    
    text += `\n【政治・経済】\n`
    text += `政治: ${result.features?.politics || (result as any).politicsEconomy?.politics || ''}\n`
    text += `経済: ${result.features?.economy || (result as any).politicsEconomy?.economy || ''}\n`
    
    text += `\n【歴史】\n`
    text += `創立物語: ${result.history.foundingStory}\n`
    if (result.history.timeline?.length) {
      text += `\n年表:\n`
      result.history.timeline.forEach(event => {
        text += `  ${event.year}: ${event.event}\n`
      })
    }
    
    // メモはオプション
    // if (community.memo) {
    //   text += `\n【メモ】\n${community.memo}\n`
    // }
    
    if (community.tags && community.tags.length > 0) {
      text += `\nタグ: ${community.tags.join(', ')}\n`
    }
    
    text += `\n作成日: ${new Date(community.createdAt).toLocaleDateString('ja-JP')}\n`
    if (community.updatedAt) {
      text += `更新日: ${new Date(community.updatedAt).toLocaleDateString('ja-JP')}\n`
    }
    
    text += '\n' + '='.repeat(50) + '\n\n'
  })

  return text
}